import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeftIcon } from "@radix-ui/react-icons";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import {
  getRequest,
  optimisticOptions,
  postRequest,
  queries,
} from "../../../../react-query";
import { toast } from "react-toastify";
import getStorage from "../../../../services/storageService";

interface ActivityStatus {
  activity_name: string;
  is_activity_active: boolean;
}

export const ActivateActivity = () => {
  const navigate = useNavigate();
  const { uin_id } = useParams();


  const { data: activitiesData = {}, isLoading}: any = useQuery(
    [queries.activities, uin_id],
    () => getRequest(`/config/activities-status/${uin_id}`),
    {
      enabled: !!uin_id,
    }
  );

    // Destructuring Data
    const { data: activities = [], pagination } = activitiesData;

    const totalPages = useMemo(
      () => Math.ceil(pagination?.totalCount / pagination?.pageSize),
      [pagination]
    );



  const { mutate: updateActivityStatus } = useMutation(
    postRequest,
    optimisticOptions(queries.activities)
  );

  const handleActivityStatusUpdate = useCallback((activityName: string, newStatus: boolean) => {
    if (!uin_id) return;
    
    updateActivityStatus({
      endPoint: `/config/activity-status/${uin_id}`,
      payload: {
        activity_name: activityName,
        is_activity_active: newStatus,
      }
    });
  }, [uin_id, updateActivityStatus]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-white w-full h-full p-8 space-y-6">
      <div className="flex w-full items-center justify-between border-b pb-4">
        <div className="flex items-center space-x-4">
          <ArrowLeftIcon
            onClick={() => navigate("/settings")}
            className="w-6 h-6 text-gray-600 hover:text-blue-500 cursor-pointer transition-colors duration-200"
          />
          <h1 className="text-2xl font-bold text-gray-800">Manage Activity Status</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {activities.map((activity:ActivityStatus) => (
          <div 
            key={activity.activity_name}
            className="bg-white border rounded-lg shadow-sm overflow-hidden transition-all duration-200 hover:shadow-md"
          >
            <div className="p-4 border-b bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-800">
                {activity.activity_name.split('_').map(word => 
                  word.charAt(0).toUpperCase() + word.slice(1)
                ).join(' ')}
              </h3>
            </div>
            <div className="p-5 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div 
                  className={`h-3 w-3 rounded-full ${activity.is_activity_active ? 'bg-green' : 'bg-red-500'}`}
                ></div>
                <span className={`text-sm font-medium ${activity.is_activity_active ? 'text-green' : 'text-red-600'}`}>
                  {activity.is_activity_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <button
                onClick={() => handleActivityStatusUpdate(
                  activity.activity_name, 
                  !activity.is_activity_active
                )}
                
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200  opacity-70 cursor-pointer ${
                  activity.is_activity_active
                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                    : 'bg-green text-white hover:opacity-80'
                }`}
              >
                {activity.is_activity_active ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

